import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  IconButton, 
  Avatar, 
  Menu, 
  MenuItem,
  ListItemIcon,
  ListItemText,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Modal,
  Button,
  Fade,
  Backdrop,
  Divider,
  Alert,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Chip,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import BusinessIcon from '@mui/icons-material/Business';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import SyncIcon from '@mui/icons-material/Sync';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import EditIcon from '@mui/icons-material/Edit';
import DescriptionIcon from '@mui/icons-material/Description';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExtensionIcon from '@mui/icons-material/Extension';
import SuperAdminLayout from './SuperAdminLayout';
import CompanyCard from './CompanyCard';
import SuperAdminService, { AiChatCompanyRow, BranchOption, DealerRow, FiscalDeviceWithCompany, MyQuotaInfo } from '../../services/SuperAdminService';
import IntegrationsOverviewModal from './IntegrationsOverviewModal';
import FiscalDeviceFormDialog from './FiscalDeviceFormDialog';
import { REMINDER_TAB_QUERY, REMINDER_SEARCH_QUERY } from './superAdminReminderLink';
import LiveSupportPanel from './LiveSupportPanel';
import AiChatSettingsDialog from './AiChatSettingsDialog';
import ModuleSettingsDialog from './ModuleSettingsDialog';
import CompanyProductsEditor, { type CompanyProductsEditorHandle } from './CompanyProductsDialog';
import CompanyDetailsModal from './CompanyDetailsModal';
import FiscalDeviceModal from './FiscalDeviceModal';
import CreateCompanyDialog from './CreateCompanyDialog';
import { AppDatePicker } from '../common/AppDatePicker';
import { BRAND } from '../../config/brand';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';

interface Company {
  id: string;
  name: string;
  logo?: string;
  admin: {
    name: string;
    email: string;
    phone?: string;
  };
  maxPersonnel?: number;
  createdAt?: string;
  /** Şirket bir bayiye atanmışsa (backend GET companies yanıtından). */
  dealerId?: number | null;
  dealerName?: string | null;
  /** Demo bitiş tarihi (ISO); null = demo değil. demoExpired sunucuda hesaplanır. */
  demoUntil?: string | null;
  demoExpired?: boolean;
  subscriptionUntil?: string | null;
  subscriptionSource?: 'google_play' | 'app_store' | null;
  subscriptionAutoRenewing?: boolean | null;
  demoCarryOverDays?: number | null;
  accessState?: 'none' | 'active' | 'grace' | 'expired' | null;
  graceUntil?: string | null;
  /** Bayinin açtığı şirkete merkez henüz hiç modül izni vermedi (tavan boş liste). */
  awaitingModules?: boolean;
}

// Detaylı şirket bilgileri için ayrı interface (modal için)
interface CompanyWithStats extends Company {
  stats: {
    personnelCount: number;
    branchCount: number;
    activeTasks: number;
    completedTasks: number;
    shifts: number;
    surveys: number;
  };
}

const ModalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
  outline: 'none',
  overflow: 'hidden'
};

// ============================================================================
// LİSTE GÖRÜNÜMÜ (üst seviye bileşen)
// ----------------------------------------------------------------------------
// Önceden bu bileşen SuperAdmin fonksiyonunun İÇİNDE tanımlıydı ve her parent
// render'ında yeni bir bileşen tipi olarak görülüp tablo remount oluyordu.
// Bu yüzden "İşlemler" menüsünün anchorEl'i DOM'dan kopmuş butona işaret edip
// menü ekranın sol üstünde (0,0) açılıyordu. Bileşen üst seviyeye taşınarak
// remount engellendi; menü artık tıklanan butonun yanında açılır.
// ============================================================================
interface CompanyListViewProps {
  companies: Company[];
  isSuperAdmin: boolean;
  contracts: Record<string, { daysRemaining?: number; isExpired?: boolean; endDate?: number } | null>;
  syncingCompanyId: string | null;
  selectedTableCompany: Company | null;
  tableMenuAnchorEl: HTMLElement | null;
  /** Seçili şirket için AI Chat asistanı açık mı (menü etiketi için). */
  selectedAiChatEnabled: boolean;
  onRowClick: (company: Company) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, company: Company) => void;
  onMenuClose: () => void;
  onMenuEdit: () => void;
  onMenuFiscalDevices: () => void;
  onMenuContract: () => void;
  onMenuAiChat: () => void;
  onMenuModules: () => void;
  onMenuAssignDealer: () => void;
  onMenuSyncToSql: () => void;
  onMenuDelete: () => void;
}

const remainingDaysChip = (days: number | null, expired?: boolean, emptyLabel = '-') => {
  if (days === null && !expired) return emptyLabel;
  if (expired || (days !== null && days < 0)) {
    return <Chip label={days !== null && days < 0 ? `${Math.abs(days)} gün geçmiş` : 'Süresi doldu'} size="small" color="error" />;
  }
  if (days === 0) return <Chip label="Bugün bitiyor" size="small" color="warning" />;
  if (days !== null && days <= 30) return <Chip label={`${days} gün`} size="small" color="warning" />;
  return <Chip label={`${days} gün`} size="small" color="success" variant="outlined" />;
};

const companyAccessLocked = (company: Company) =>
  company.demoExpired === true || company.accessState === 'expired';

const subscriptionStatusChip = (company: Company) => {
  if (!company.subscriptionUntil && !company.subscriptionSource) return null;
  if (company.accessState === 'grace') {
    return <Chip label="Süre doldu (son gün)" size="small" color="warning" />;
  }
  if (company.accessState === 'expired') {
    return <Chip label="Abonelik bitti" size="small" color="error" />;
  }
  const end = company.subscriptionUntil
    ? new Date(company.subscriptionUntil).toLocaleDateString('tr-TR')
    : '';
  return <Chip label={end ? `Mobil: ${end}` : 'Mobil abonelik'} size="small" color="info" variant="outlined" />;
};

const demoRemainingDays = (company: Company): { days: number | null; expired: boolean } => {
  if (!company.demoUntil) return { days: null, expired: false };
  // Grace: demoUntil geçmiş olsa da kilit henüz yok; "gün geçmiş" gösterme.
  if (company.accessState === 'grace') return { days: 0, expired: false };
  const days = Math.ceil((new Date(company.demoUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return { days, expired: company.demoExpired === true || company.accessState === 'expired' || days < 0 };
};

const CompanyListView: React.FC<CompanyListViewProps> = ({
  companies,
  isSuperAdmin,
  contracts,
  syncingCompanyId,
  selectedTableCompany,
  tableMenuAnchorEl,
  selectedAiChatEnabled,
  onRowClick,
  onMenuClick,
  onMenuClose,
  onMenuEdit,
  onMenuFiscalDevices,
  onMenuContract,
  onMenuAiChat,
  onMenuModules,
  onMenuAssignDealer,
  onMenuSyncToSql,
  onMenuDelete,
}) => {
  const columns = React.useMemo<DataTableColumn<Company>[]>(() => {
    const cols: DataTableColumn<Company>[] = [
      {
        id: 'rowNumber',
        label: 'SN',
        width: '56px',
        align: 'center',
        render: (_row, index) => index + 1,
      },
      {
        id: 'demoDays',
        label: 'Kalan Süre',
        width: '130px',
        render: (row) => {
          const demo = demoRemainingDays(row);
          const demoNode = row.demoUntil ? remainingDaysChip(demo.days, demo.expired) : null;
          const subNode = subscriptionStatusChip(row);
          if (!demoNode && !subNode) return '-';
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
              {demoNode}
              {subNode}
            </Box>
          );
        },
      },
      {
        id: 'name',
        label: 'Şirket',
        minWidth: '180px',
        tooltip: (row) => row.name,
        cellSx: { fontWeight: 500 },
        render: (row) => row.name,
      },
      { id: 'admin', label: 'Admin', render: (row) => row.admin?.name || '-' },
      {
        id: 'email',
        label: 'Email',
        minWidth: '180px',
        tooltip: (row) => row.admin?.email || '',
        render: (row) => row.admin?.email || '-',
      },
      { id: 'phone', label: 'Telefon', render: (row) => row.admin?.phone || '-' },
      {
        id: 'createdAt',
        label: 'Kayıt Tarihi',
        render: (row) => (row.createdAt ? new Date(row.createdAt).toLocaleDateString('tr-TR') : '-'),
      },
      {
        id: 'contract',
        label: 'Sözleşme',
        width: '130px',
        render: (row) => {
          const contract = contracts[row.id];
          if (!contract) return '-';
          return remainingDaysChip(contract.daysRemaining ?? null, contract.isExpired);
        },
      },
      { id: 'maxPersonnel', label: 'Max Personel', align: 'center', render: (row) => row.maxPersonnel || 0 },
    ];
    if (isSuperAdmin) {
      cols.push({
        id: 'dealer',
        label: 'Bayi',
        render: (row) => row.dealerName || '-',
      });
    }
    cols.push({
      id: 'actions',
      label: 'İşlemler',
      width: '80px',
      align: 'center',
      render: (row) => (
        <Tooltip title="İşlemler">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              onMenuClick(e, row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    });
    return cols;
  }, [onMenuClick, contracts, isSuperAdmin]);

  return (
    <>
      <AccountingDataTable<Company>
        columns={columns}
        rows={companies}
        getRowId={(row) => row.id}
        onRowClick={onRowClick}
        clickable
        tableLayout="auto"
        ariaLabel="Şirketler"
        defaultRowsPerPage={25}
        rowsPerPageOptions={[25, 50, 100]}
        emptyMessage="Şirket bulunamadı."
        getRowSx={(row) => {
          const demo = demoRemainingDays(row);
          if (demo.expired || (demo.days !== null && demo.days < 0)) {
            return { bgcolor: 'rgba(244,67,54,0.08)', '&:hover': { bgcolor: 'rgba(244,67,54,0.14)' } };
          }
          if (demo.days !== null && demo.days <= 30) {
            return { bgcolor: 'rgba(255,152,0,0.08)', '&:hover': { bgcolor: 'rgba(255,152,0,0.14)' } };
          }
          return {};
        }}
      />

      <Menu
        anchorEl={tableMenuAnchorEl}
        open={Boolean(tableMenuAnchorEl)}
        onClose={onMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={onMenuEdit}>
          <ListItemIcon>
            <PeopleAltIcon fontSize="small" sx={{ color: '#1976d2' }} />
          </ListItemIcon>
          <ListItemText>Personel Sayısını Güncelle</ListItemText>
        </MenuItem>
        {isSuperAdmin && (
          <MenuItem onClick={onMenuFiscalDevices}>
            <ListItemIcon>
              <PointOfSaleIcon fontSize="small" sx={{ color: '#ff9800' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#ff9800' }}>Yazarkasa Cihazları</ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={onMenuContract}>
          <ListItemIcon>
            <DescriptionIcon fontSize="small" sx={{ color: '#66bb6a' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#66bb6a' }}>
            {selectedTableCompany && contracts[selectedTableCompany.id] ? 'Sözleşmeyi Güncelle' : 'Sözleşme Yapıldı'}
          </ListItemText>
        </MenuItem>
        {isSuperAdmin && (
          <MenuItem onClick={onMenuAiChat}>
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: '#9c27b0' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#9c27b0' }}>
              AI Chat {selectedAiChatEnabled ? '(Açık)' : '(Kapalı)'}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={onMenuModules}>
          <ListItemIcon>
            <ExtensionIcon fontSize="small" sx={{ color: '#0288d1' }} />
          </ListItemIcon>
          <ListItemText sx={{ color: '#0288d1' }}>Modüller</ListItemText>
        </MenuItem>
        {isSuperAdmin && (
          <MenuItem onClick={onMenuAssignDealer}>
            <ListItemIcon>
              <StorefrontIcon fontSize="small" sx={{ color: '#7b1fa2' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#7b1fa2' }}>
              {selectedTableCompany?.dealerName ? `Bayi: ${selectedTableCompany.dealerName}` : 'Bayiye Ata'}
            </ListItemText>
          </MenuItem>
        )}
        {isSuperAdmin && (
          <MenuItem
            onClick={onMenuSyncToSql}
            disabled={!!syncingCompanyId && syncingCompanyId === selectedTableCompany?.id}
          >
            <ListItemIcon>
              {syncingCompanyId && syncingCompanyId === selectedTableCompany?.id ? (
                <CircularProgress size={16} sx={{ color: '#1976d2' }} />
              ) : (
                <CloudUploadIcon fontSize="small" sx={{ color: '#1976d2' }} />
              )}
            </ListItemIcon>
            <ListItemText sx={{ color: '#1976d2' }}>SQL'e Senkronize Et</ListItemText>
          </MenuItem>
        )}
        {isSuperAdmin && <Divider />}
        {isSuperAdmin && (
          <MenuItem onClick={onMenuDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText sx={{ color: '#f44336' }}>Şirketi Sil</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

const SuperAdmin: React.FC = () => {
  const { currentUser, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'card' | 'list'>('list');
  const [apiWarning, setApiWarning] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [newMaxPersonnel, setNewMaxPersonnel] = useState<number>(0);
  const [updating, setUpdating] = useState(false);
  
  // Şirket silme ile ilgili state'ler
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState<{id: string, name: string} | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<string[]>([]);
  
  // Şirket detayları modalı için state'ler
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsCompany, setDetailsCompany] = useState<Company | null>(null);

  // SQL'e Senkronize Et — snackbar ve loading state
  const [syncSnackbar, setSyncSnackbar] = useState<{
    open: boolean;
    severity: 'info' | 'success' | 'error';
    message: string;
  }>({ open: false, severity: 'info', message: '' });
  const [syncingCompanyId, setSyncingCompanyId] = useState<string | null>(null);
  
  // Tab state — indeks yerine açık value kullanılır (rol koşullu sekmelerde indeks kayar).
  // 'demo' her iki rolde de görünür; diğer özel sekmeler yalnız superadmin.
  type TabValue = 'all' | 'demo' | 'active' | 'contracts' | 'fiscal-companies' | 'fiscal-devices' | 'dealer-fiscal-devices';
  const [currentTab, setCurrentTab] = useState<TabValue>('all');
  
  // Aktif şirketler için state'ler
  const [activeCompanies, setActiveCompanies] = useState<Company[]>([]);
  const [activeCompaniesLoading, setActiveCompaniesLoading] = useState(false);
  const [loginActivities, setLoginActivities] = useState<Record<string, any>>({});
  
  // Sözleşme yönetimi için state'ler
  const [contracts, setContracts] = useState<Record<string, any>>({});
  const [isContractModalOpen, setIsContractModalOpen] = useState(false);
  const [selectedCompanyForContract, setSelectedCompanyForContract] = useState<Company | null>(null);
  const [contractStartDate, setContractStartDate] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [savingContract, setSavingContract] = useState(false);
  
  // AI chat asistanı yönetimi (şirket bazlı aç/kapat + limit + kullanım)
  const [aiChatRows, setAiChatRows] = useState<Record<string, AiChatCompanyRow>>({});
  const [aiChatCompany, setAiChatCompany] = useState<{ id: string; name: string } | null>(null);

  // Modül yönetimi diyaloğu (şirket bazlı modül aç/kapa)
  const [modulesCompany, setModulesCompany] = useState<{ id: string; name: string } | null>(null);
  // Sözleşme penceresindeki ürün fiyatları
  const productsEditorRef = useRef<CompanyProductsEditorHandle>(null);
  const [productsReady, setProductsReady] = useState(true);
  const handleProductsReady = useCallback((ready: boolean) => setProductsReady(ready), []);

  // Şirket oluşturma diyaloğu (superadmin + bayi — bayi şirketi otomatik kendine atanır)
  const [isCreateCompanyOpen, setIsCreateCompanyOpen] = useState(false);

  // Bayinin kendi şirket açma kotası. Bayi kotasını başka yerden göremiyor
  // (bayi listesi ucu süper admine kapalı), o yüzden panelde gösterilir.
  const [myQuota, setMyQuota] = useState<MyQuotaInfo | null>(null);

  // "Bayiye Ata" kısayolu (yalnız süper admin) — kart menüsünden açılır
  const [assignDealerCompany, setAssignDealerCompany] = useState<Company | null>(null);
  const [dealersList, setDealersList] = useState<DealerRow[]>([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<DealerRow | null>(null);
  const [assigningDealer, setAssigningDealer] = useState(false);

  // "Tüm Şirketler" sekmesi filtreleri. Arama kutusu ada göre serbest metin
  // eşleştiriyor; bunlar ise tek bir şirkete ya da bir bayinin portföyüne
  // kesin olarak daraltmak için.
  const [companyFilter, setCompanyFilter] = useState<{ id: string; name: string } | null>(null);
  const [dealerFilter, setDealerFilter] = useState<DealerRow | null>(null);
  // Tüm şirket sekmelerinde ortak: son giriş tarihi aralığı, kalan demo günü, aktif/pasif (demo kilit).
  const [usageStartDate, setUsageStartDate] = useState('');
  const [usageEndDate, setUsageEndDate] = useState('');
  const [demoDaysFilter, setDemoDaysFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Bayi listesi hem "Bayiye Ata" diyaloğu hem bayi filtresi tarafından
  // kullanılıyor; iki yerden de aynı tek seferlik yükleme çağrılır.
  const ensureDealersLoaded = React.useCallback(async () => {
    if (dealersList.length > 0 || dealersLoading) return;
    try {
      setDealersLoading(true);
      const list = await SuperAdminService.getInstance().getDealers();
      setDealersList(list);
    } catch (error) {
      console.error('❌ [SuperAdmin] Bayi listesi alınamadı:', error);
    } finally {
      setDealersLoading(false);
    }
  }, [dealersList.length, dealersLoading]);

  // Kota bilgisini tazeler. Süper adminde sınır olmadığı için hiç çağrılmaz.
  const reloadMyQuota = React.useCallback(async () => {
    if (isSuperAdmin) return;
    try {
      const info = await SuperAdminService.getInstance().getMyQuota();
      setMyQuota(info);
    } catch (error) {
      // Kota göstergesi bilgilendirme amaçlı; alınamazsa panel çalışmaya devam eder.
      console.error('❌ [SuperAdmin] Kota bilgisi alınamadı:', error);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    reloadMyQuota();
  }, [reloadMyQuota]);

  // Şirket listesini sessizce yeniden yükler (oluşturma/atama sonrası).
  // Şirket sayısı değiştiği için kota göstergesi de tazelenir.
  const reloadCompanies = React.useCallback(async () => {
    try {
      const result = await SuperAdminService.getInstance().getAllCompanies();
      setCompanies(result);
      await reloadMyQuota();
    } catch (error) {
      console.error('❌ [SuperAdmin] Şirket listesi yenilenirken hata:', error);
    }
  }, [reloadMyQuota]);

  // Yazarkasa cihazları için state'ler
  const [isFiscalDeviceModalOpen, setIsFiscalDeviceModalOpen] = useState(false);
  const [fiscalDeviceCompany, setFiscalDeviceCompany] = useState<{id: string, name: string} | null>(null);
  const [companiesWithDevices, setCompaniesWithDevices] = useState<string[]>([]);
  const [fiscalDevicesLoading, setFiscalDevicesLoading] = useState(false);
  
  // Tüm yazarkasalar tab'ı için state'ler (server-side arama + sayfalama)
  const [allFiscalDevices, setAllFiscalDevices] = useState<FiscalDeviceWithCompany[]>([]);
  const [allFiscalDevicesLoading, setAllFiscalDevicesLoading] = useState(false);
  const [fiscalSearchTerm, setFiscalSearchTerm] = useState('');
  const [fiscalDebouncedSearch, setFiscalDebouncedSearch] = useState('');
  const [fiscalPage, setFiscalPage] = useState(0); // 0-bazlı (TablePagination)
  const [fiscalPageSize, setFiscalPageSize] = useState(100);
  const [fiscalTotalCount, setFiscalTotalCount] = useState(0);
  const [fiscalCompanyFilter, setFiscalCompanyFilter] = useState<{ id: string; name: string } | null>(null);
  // Şube filtresi yalnızca şirket seçiliyken anlamlı; seçenekler o şirketin
  // tenant veritabanından geldiği için şirket değişince baştan yüklenir.
  const [fiscalBranchFilter, setFiscalBranchFilter] = useState<BranchOption | null>(null);
  const [fiscalBranchOptions, setFiscalBranchOptions] = useState<BranchOption[]>([]);
  const [fiscalBranchOptionsLoading, setFiscalBranchOptionsLoading] = useState(false);
  // "Bayi Yazarkasaları" sekmesinde tek bayiye daraltma (yalnız süper admin).
  const [fiscalDealerFilter, setFiscalDealerFilter] = useState<DealerRow | null>(null);

  // Entegrasyon genel bakış modalı
  const [isIntegrationsModalOpen, setIsIntegrationsModalOpen] = useState(false);

  // Yazarkasa ekleme/düzenleme (sekme içinden; şirket kartına gitmeye gerek yok)
  const [fiscalFormOpen, setFiscalFormOpen] = useState(false);
  const [fiscalFormDevice, setFiscalFormDevice] = useState<FiscalDeviceWithCompany | null>(null);

  // Arama terimini debounce et (her tuşta backend'e gitmemek için)
  useEffect(() => {
    const timer = setTimeout(() => {
      setFiscalDebouncedSearch(fiscalSearchTerm);
      setFiscalPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [fiscalSearchTerm]);

  // Bildirimden gelen filtre — hangi sekmeye hangi metinle gidildiği. Kullanıcı
  // bu filtreyi tek tıkla kaldırabilsin diye ayrıca tutuluyor; arama kutusundaki
  // metin elle değiştirilirse gösterge kendiliğinden kaybolur.
  const [reminderFilter, setReminderFilter] = useState<{ text: string; tab: string } | null>(null);

  // Yenileme hatırlatmalarından gelen derin bağlantı: ?tab=fiscal-devices&q=<seri no>.
  // Parametreler uygulandıktan sonra URL temizlenir; böylece kullanıcı aramayı
  // değiştirdiğinde adres çubuğu eski cihazı göstermeye devam etmez ve aynı
  // bildirime tekrar tıklanınca yönlendirme yeniden tetiklenebilir.
  useEffect(() => {
    if (!location.search) return;
    const params = new URLSearchParams(location.search);
    const tab = params.get(REMINDER_TAB_QUERY);
    const query = params.get(REMINDER_SEARCH_QUERY);
    if (!tab && !query) return;

    if (tab) setCurrentTab(tab as TabValue);
    if (query !== null) {
      // Yazarkasa sekmeleri (merkez ve bayi) sunucu taraflı kendi aramasını
      // kullanır; diğer sekmeler şirket kartlarını ortak arama kutusuyla filtreler.
      if (tab === 'fiscal-devices' || tab === 'dealer-fiscal-devices') {
        setFiscalSearchTerm(query);
        setFiscalDebouncedSearch(query);
        setFiscalPage(0);
      } else {
        setSearchTerm(query);
      }
      setReminderFilter(query ? { text: query, tab: tab || 'all' } : null);
    }
    navigate('/superadmin', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const startTime = Date.now();
        
        console.log('🚀 [SuperAdmin] Şirketler yükleniyor...');

        // Rol koruması App.tsx'teki RequireRole guard'ında (superadmin|dealer) —
        // sayfa içi ad-hoc kontrol kaldırıldı. Veri backend'den role göre scoped gelir.
        const afterRoleCheck = Date.now();
        
        // API üzerinden şirketleri al
        const apiStartTime = Date.now();
        const superAdminService = SuperAdminService.getInstance();
        const result = await superAdminService.getAllCompanies();
        const apiEndTime = Date.now();
        
        console.log('📊 [SuperAdmin] API Yanıtı:');
        console.log(`   └─ Şirket Sayısı: ${result.length}`);
        console.log(`   └─ API İstek Süresi: ${apiEndTime - apiStartTime}ms (network + backend)`);
        console.log(`   └─ Veri Yapısı: Sadece ID + /info (personnel, tasks, branches YOK)`);
        
        // State güncelleme süresi
        const beforeStateUpdate = Date.now();
        console.log('🔄 [SuperAdmin] State güncelleniyor...');
        
        // Eğer API'den warning gelirse, onu göster
        if (result.length === 0) {
          console.log('⚠️ [SuperAdmin] companies_index tablosu boş!');
          setApiWarning('companies_index tablosu boş. Eski şirketlerin ID\'lerini elle eklemeniz gerekiyor.');
        } else {
          setApiWarning(null);
        }
        
        setCompanies(result);
        
        const afterStateUpdate = Date.now();
        const totalTime = Date.now() - startTime;
        
        console.log('✅ [SuperAdmin] Şirketler başarıyla yüklendi!');
        console.log('⏱️ [SuperAdmin] PERFORMANS ANALİZİ:');
        console.log(`   ├─ Rol Kontrolü: ${afterRoleCheck - startTime}ms (AuthContext'ten)`);
        console.log(`   ├─ API İsteği (network + backend): ${apiEndTime - apiStartTime}ms`);
        console.log(`   ├─ State Güncelleme: ${afterStateUpdate - beforeStateUpdate}ms`);
        console.log(`   └─ TOPLAM: ${totalTime}ms`);
        
      } catch (error) {
        console.error('❌ [SuperAdmin] Şirketler yüklenirken hata:', error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchCompanies();
    }
  }, [currentUser, navigate]);

  // Sözleşmeleri yükle
  useEffect(() => {
    const fetchContracts = async () => {
      try {
        const superAdminService = SuperAdminService.getInstance();
        const result = await superAdminService.getAllContracts();
        
        if (result.success) {
          setContracts(result.contracts || {});
          console.log(`✅ [Sözleşmeler] ${result.count} sözleşme yüklendi`);
        }
      } catch (error) {
        console.error('❌ [Sözleşmeler] Yüklenirken hata:', error);
      }
    };

    // Sözleşme uçları artık AdminScope: bayi de çağırabilir ve yalnızca kendi
    // şirketlerinin sözleşmelerini alır (kapsam backend'de zorlanır).
    if (currentUser && companies.length > 0) {
      fetchContracts();
    }
  }, [currentUser, companies]);

  // AI chat ayar/kullanım satırlarını yükle (ana GTS DB — SuperAdminAiChatController)
  const fetchAiChatRows = React.useCallback(async () => {
    try {
      const rows = await SuperAdminService.getInstance().getAiChatCompanies();
      const map: Record<string, AiChatCompanyRow> = {};
      rows.forEach((r) => {
        map[r.companyId] = r;
      });
      setAiChatRows(map);
    } catch (error) {
      console.error('❌ [AI Chat] Ayarlar yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    // AI chat yönetimi süper admine özel (backend SuperAdminOnly) — bayi için çağrılmaz.
    if (currentUser && isSuperAdmin && companies.length > 0) {
      fetchAiChatRows();
    }
  }, [currentUser, isSuperAdmin, companies, fetchAiChatRows]);

  const handleViewDetails = (companyId: string) => {
    // Şirket detayları sayfasına yönlendir
    navigate(`/superadmin/company/${companyId}`);
  };

  // "Bayiye Ata" diyaloğunu açar; bayi listesi ilk açılışta yüklenir (yalnız süper admin).
  const handleOpenAssignDealer = async (company: Company) => {
    setAssignDealerCompany(company);
    setSelectedDealer(null);
    await ensureDealersLoaded();
  };

  const closeAssignDealer = () => {
    if (assigningDealer) return;
    setAssignDealerCompany(null);
    setSelectedDealer(null);
  };

  const handleAssignDealer = async () => {
    if (!assignDealerCompany || !selectedDealer || assigningDealer) return;
    try {
      setAssigningDealer(true);
      await SuperAdminService.getInstance().assignCompanyToDealer(
        selectedDealer.id,
        assignDealerCompany.id
      );
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${assignDealerCompany.name} → ${selectedDealer.name} ataması yapıldı.`,
      });
      setAssignDealerCompany(null);
      setSelectedDealer(null);
      await reloadCompanies();
    } catch (error: any) {
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Şirket bayiye atanamadı.',
      });
    } finally {
      setAssigningDealer(false);
    }
  };

  const handleUnassignDealer = async () => {
    if (!assignDealerCompany?.dealerId || assigningDealer) return;
    try {
      setAssigningDealer(true);
      await SuperAdminService.getInstance().unassignCompanyFromDealer(
        assignDealerCompany.dealerId,
        assignDealerCompany.id
      );
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${assignDealerCompany.name} bayi ataması kaldırıldı.`,
      });
      setAssignDealerCompany(null);
      setSelectedDealer(null);
      await reloadCompanies();
    } catch (error: any) {
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: error?.message || 'Bayi ataması kaldırılamadı.',
      });
    } finally {
      setAssigningDealer(false);
    }
  };

  const handleOpenModal = (company: Company) => {
    setSelectedCompany(company);
    setNewMaxPersonnel(company.maxPersonnel || 0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  const handleUpdateMaxPersonnel = async () => {
    if (!selectedCompany) return;
    
    try {
      setUpdating(true);
      
      // API üzerinden güncelle
      const superAdminService = SuperAdminService.getInstance();
      await superAdminService.updateMaxPersonnel(selectedCompany.id, newMaxPersonnel);
      
      // Yerel state'i güncelle
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company.id === selectedCompany.id 
            ? { ...company, maxPersonnel: newMaxPersonnel } 
            : company
        )
      );
      
      handleCloseModal();
    } catch (error) {
      console.error('Personel sayısı güncellenirken hata:', error);
      alert('Personel sayısı güncellenirken bir hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteCompany = (companyId: string, companyName: string) => {
    setCompanyToDelete({ id: companyId, name: companyName });
    setIsDeleteModalOpen(true);
    setDeleteConfirmation('');
    setDeleteProgress([]);
  };

  // Firebase'deki firma bilgilerini SQL Companies tablosuna senkronize et
  const handleSyncCompanyToSql = async (companyId: string, companyName: string) => {
    if (syncingCompanyId === companyId) return;
    try {
      setSyncingCompanyId(companyId);
      setSyncSnackbar({
        open: true,
        severity: 'info',
        message: `${companyName} senkronize ediliyor...`,
      });
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.syncCompanyToSql(companyId);
      setSyncSnackbar({
        open: true,
        severity: 'success',
        message: `${companyName} SQL'e senkronize edildi (sqlId=${result.sqlId})`,
      });
    } catch (err: any) {
      console.error('SQL senkronizasyon hatasi:', err);
      const apiMsg = err?.response?.data?.message || err?.message || 'Bilinmeyen hata';
      setSyncSnackbar({
        open: true,
        severity: 'error',
        message: `Senkronize edilemedi: ${apiMsg}`,
      });
    } finally {
      setSyncingCompanyId(null);
    }
  };
  
  // Şirketin sözleşmesi var mı kontrol et
  const hasActiveContract = (companyId: string): boolean => {
    const contract = contracts[companyId];
    return contract !== undefined && contract !== null;
  };

  const closeDeleteModal = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setCompanyToDelete(null);
      setDeleteConfirmation('');
      setDeleteProgress([]);
    }
  };

  const confirmDelete = async () => {
    console.log('🗑️ [Delete] confirmDelete çağrıldı');
    console.log('🗑️ [Delete] companyToDelete:', companyToDelete);
    console.log('🗑️ [Delete] deleteConfirmation:', deleteConfirmation);
    
    if (!companyToDelete) {
      console.error('❌ [Delete] companyToDelete null!');
      return;
    }
    
    if (deleteConfirmation !== companyToDelete.name) {
      console.error('❌ [Delete] Şirket adı eşleşmiyor!', {
        expected: companyToDelete.name,
        received: deleteConfirmation
      });
      return;
    }

    try {
      console.log('✅ [Delete] Silme işlemi başlatılıyor...');
      setIsDeleting(true);
      setDeleteProgress([`Şirket silme işlemi başlatılıyor: ${companyToDelete.name} (${companyToDelete.id})...`]);

      // API üzerinden şirketi sil
      const superAdminService = SuperAdminService.getInstance();
      console.log('🔄 [Delete] API çağrısı yapılıyor:', companyToDelete.id);
      
      const result = await superAdminService.deleteCompany(companyToDelete.id);
      console.log('✅ [Delete] API yanıtı alındı:', result);
      
      // API'den dönen logları ekle
      setDeleteProgress(prev => [...prev, ...result.log]);
      
      // Companies listesini güncelle
      setCompanies(prevCompanies => prevCompanies.filter(company => company.id !== companyToDelete.id));
      
      // İşlem tamamlandı
      setDeleteProgress(prev => [...prev, `İşlem tamamlandı.`]);
      
      console.log('✅ [Delete] Silme işlemi tamamlandı, 5 saniye sonra modal kapanacak');
      
      // 5 saniye sonra modalı kapat
      setTimeout(() => {
        setIsDeleting(false);
        setIsDeleteModalOpen(false);
        setCompanyToDelete(null);
        setDeleteConfirmation('');
        setDeleteProgress([]);
      }, 5000);
      
    } catch (error: any) {
      console.error('❌ [Delete] Şirket silinirken hata:', error);
      console.error('❌ [Delete] Hata detayı:', {
        message: error?.message,
        response: error?.response,
        stack: error?.stack
      });
      setDeleteProgress(prev => [...prev, `Kritik hata: ${error?.message || error}`]);
      setIsDeleting(false);
    }
  };

  // Tab değiştir
  const handleTabChange = (event: React.SyntheticEvent, newValue: TabValue) => {
    setCurrentTab(newValue);

    // Şirket/bayi filtreleri yalnızca "Tüm Şirketler" sekmesinde gösteriliyor;
    // sekmeden çıkınca temizlenmezse görünmeyen bir filtre listeyi daraltırdı.
    if (newValue !== 'all') {
      setCompanyFilter(null);
      setDealerFilter(null);
    }

    
    // Aktif Şirketler sekmesine geçildiğinde verileri yükle
    if (newValue === 'active' && activeCompanies.length === 0) {
      loadActiveCompanies();
    }
    
    // Yazarkasa verilen şirketler + Yazarkasalar filtresi aynı ID listesini kullanır
    if ((newValue === 'fiscal-companies' || newValue === 'fiscal-devices') && companiesWithDevices.length === 0) {
      loadCompaniesWithFiscalDevices();
    }
    
    // Yazarkasalar sekmesi: yükleme aşağıdaki useEffect ile
    // (sayfa/arama değişimlerini de kapsayacak şekilde) yapılır.
  };

  // Kullanım filtresi ve "Aktif Şirketler" sekmesi aynı login kaydını paylaşır.
  const applyLoginActivities = React.useCallback((rows: any[], companyList: Company[]) => {
    const activitiesMap: Record<string, any> = {};
    rows.forEach((activity: any) => {
      if (activity?.companyId) activitiesMap[activity.companyId] = activity;
    });
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    const activeCompaniesList = companyList.filter((company) => {
      const lastLogin = activitiesMap[company.id]?.lastLogin;
      return typeof lastLogin === 'number' && lastLogin >= sevenDaysAgo;
    });
    setLoginActivities(activitiesMap);
    setActiveCompanies(activeCompaniesList);
    return activitiesMap;
  }, []);

  const loadLoginActivities = React.useCallback(async (companyList: Company[] = companies) => {
    try {
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.getRecentLoginActivities();
      if (result.success && result.data) {
        applyLoginActivities(result.data, companyList);
        return;
      }
      setLoginActivities({});
      setActiveCompanies([]);
    } catch (error) {
      console.error('❌ [SuperAdmin] Login activities yüklenirken hata:', error);
    }
  }, [applyLoginActivities, companies]);

  // Kullanım filtresi "Tüm Şirketler"de de çalışsın diye giriş kayıtlarını baştan yükle.
  useEffect(() => {
    if (currentUser && isSuperAdmin && companies.length > 0 && Object.keys(loginActivities).length === 0) {
      loadLoginActivities(companies);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isSuperAdmin, companies, loadLoginActivities]);

  // Aktif şirketleri yükle (Son 7 günde giriş yapmış)
  const loadActiveCompanies = async () => {
    if (activeCompaniesLoading) return;
    
    try {
      setActiveCompaniesLoading(true);
      console.log(`🔍 [Aktif Şirketler] Login activities verisi okunuyor...`);
      await loadLoginActivities(companies);
    } catch (error) {
      console.error('❌ [Aktif Şirketler] Yüklenirken hata:', error);
      alert('Aktif şirketler yüklenirken bir hata oluştu');
    } finally {
      setActiveCompaniesLoading(false);
    }
  };

  // Yazarkasa cihazı olan şirketleri yükle
  const loadCompaniesWithFiscalDevices = async () => {
    if (fiscalDevicesLoading) return;
    
    try {
      setFiscalDevicesLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const companyIds = await superAdminService.getCompaniesWithFiscalDevices();
      setCompaniesWithDevices(companyIds);
      console.log(`✅ [Yazarkasa] ${companyIds.length} şirkette cihaz bulundu`);
    } catch (error) {
      console.error('❌ [Yazarkasa] Şirketler yüklenirken hata:', error);
    } finally {
      setFiscalDevicesLoading(false);
    }
  };

  // Yazarkasa cihazları modalını aç
  const handleOpenFiscalDeviceModal = (company: Company) => {
    setFiscalDeviceCompany({ id: company.id, name: company.name });
    setIsFiscalDeviceModalOpen(true);
  };

  // Yazarkasa cihazları modalını kapat
  const handleCloseFiscalDeviceModal = () => {
    setIsFiscalDeviceModalOpen(false);
    setFiscalDeviceCompany(null);
    // Yazarkasa Verilen Şirketler sekmesi aktifse listeyi yeniden yükle
    if (currentTab === 'fiscal-companies') {
      loadCompaniesWithFiscalDevices();
    }
  };

  /** Bayi yazarkasaları sekmesi mi (yalnız süper adminde açılabilir). */
  const isDealerFiscalTab = currentTab === 'dealer-fiscal-devices';
  /** İki yazarkasa listesi sekmesi aynı tabloyu ve filtre çubuğunu paylaşır. */
  const isFiscalDeviceTab = currentTab === 'fiscal-devices' || isDealerFiscalTab;

  /**
   * Listenin hangi kaynağı göstereceği.
   * Süper admin: ana sekme yalnız merkezin verdiklerini, bayi sekmesi yalnız
   * bayilerin verdiklerini gösterir. Bayi kullanıcısında parametre gönderilmez —
   * backend kapsamı zorla bayinin kendi cihazlarına sabitler.
   */
  const fiscalSource: 'central' | 'dealer' | undefined = !isSuperAdmin
    ? undefined
    : isDealerFiscalTab
      ? 'dealer'
      : 'central';

  // Tüm yazarkasa cihazlarını yükle
  const loadAllFiscalDevices = React.useCallback(async () => {
    try {
      setAllFiscalDevicesLoading(true);
      const superAdminService = SuperAdminService.getInstance();
      const { items, totalCount } = await superAdminService.getFiscalDevicesPaged({
        page: fiscalPage + 1, // backend 1-bazlı
        pageSize: fiscalPageSize,
        search: fiscalDebouncedSearch,
        companyId: fiscalCompanyFilter?.id,
        branchId: fiscalBranchFilter?.branchId,
        source: fiscalSource,
        dealerId: fiscalDealerFilter?.id,
      });
      setAllFiscalDevices(items);
      setFiscalTotalCount(totalCount);
      console.log(`✅ [Yazarkasalar] ${items.length}/${totalCount} cihaz yüklendi (sayfa ${fiscalPage + 1}, kaynak=${fiscalSource ?? 'kapsam'})`);
    } catch (error) {
      console.error('❌ [Yazarkasalar] Cihazlar yüklenirken hata:', error);
    } finally {
      setAllFiscalDevicesLoading(false);
    }
  }, [fiscalPage, fiscalPageSize, fiscalDebouncedSearch, fiscalCompanyFilter, fiscalBranchFilter, fiscalSource, fiscalDealerFilter]);

  // Şirket seçimi değişince şube seçeneklerini tazele. Seçili şube her durumda
  // sıfırlanır: önceki şirketin şubesiyle filtrelemeye devam etmek listeyi
  // sessizce boşaltırdı.
  useEffect(() => {
    setFiscalBranchFilter(null);

    const companyId = fiscalCompanyFilter?.id;
    if (!companyId) {
      setFiscalBranchOptions([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setFiscalBranchOptionsLoading(true);
        const options = await SuperAdminService.getInstance().getCompanyBranchOptions(companyId);
        if (!cancelled) setFiscalBranchOptions(options);
      } catch (error) {
        console.error('❌ [Yazarkasalar] Şube listesi alınamadı:', error);
        if (!cancelled) setFiscalBranchOptions([]);
      } finally {
        if (!cancelled) setFiscalBranchOptionsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fiscalCompanyFilter?.id]);

  // Yazarkasalar sekmesi açıkken sayfa/arama değişimlerinde sunucudan yükle
  // (merkez ve bayi listeleri aynı yükleyiciyi paylaşır, kaynak parametresi değişir)
  useEffect(() => {
    if (isFiscalDeviceTab) {
      loadAllFiscalDevices();
      if (companiesWithDevices.length === 0) {
        loadCompaniesWithFiscalDevices();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFiscalDeviceTab, loadAllFiscalDevices]);

  // İki yazarkasa sekmesi arasında geçişte sayfa başa alınır: merkez listesinin
  // 4. sayfasından bayi listesine geçince boş sayfa görünmesin. Bayi filtresi
  // yalnız bayi sekmesinde anlamlı olduğu için sekme değişince sıfırlanır.
  useEffect(() => {
    setFiscalPage(0);
    if (!isDealerFiscalTab) {
      setFiscalDealerFilter(null);
    }
  }, [isDealerFiscalTab]);

  // Filtre ve form: yalnızca en az bir yazarkasası olan şirketler
  const fiscalCompanyOptions = React.useMemo(
    () => {
      const withDevices = new Set(companiesWithDevices);
      return companies
        .filter((c) => withDevices.has(c.id))
        .map((c) => ({ id: c.id, name: c.name }))
        .sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    },
    [companies, companiesWithDevices]
  );

  // Kalan gün hesapla
  const getDaysRemaining = (expiryDate: string | null): number | null => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    return Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Tarih formatla
  const formatDateTR = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString('tr-TR'); } catch { return '-'; }
  };

  // CompanyId'den şirket adı bul
  const getCompanyName = (companyId: string): string => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  // Yazarkasalar: arama artık sunucu tarafında (getFiscalDevicesPaged) yapılıyor.

  /**
   * Yazarkasalar tablosu kolonları. Sıralama sunucuda sabit (bitiş tarihi en
   * yakın olan üstte) olduğu için kolon başlıklarından sıralama açılmadı;
   * yalnızca açık sayfayı sıralamak listeyi yanıltıcı hale getirirdi.
   */
  const fiscalEditColumn = React.useCallback(
    (id: string): DataTableColumn<FiscalDeviceWithCompany> => ({
      id,
      label: 'İşlemler',
      width: '80px',
      align: 'center',
      render: (row) => (
        <Tooltip title="Düzenle">
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => { e.stopPropagation(); setFiscalFormDevice(row); setFiscalFormOpen(true); }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    }),
    []
  );

  const fiscalDeviceColumns = React.useMemo<DataTableColumn<FiscalDeviceWithCompany>[]>(() => [
    // Tablo geniş olduğu için satırı sonuna kadar kaydırmadan düzenlemek zor;
    // aynı buton hem başta hem sonda duruyor.
    fiscalEditColumn('actionsStart'),
    {
      id: 'rowNumber',
      label: 'SN',
      width: '56px',
      align: 'center',
      render: (_row, index) => fiscalPage * fiscalPageSize + index + 1,
    },
    {
      id: 'daysRemaining',
      label: 'Kalan Süre',
      width: '130px',
      render: (row) => {
        const days = getDaysRemaining(row.expiryDate);
        if (days === null) return <Chip label="Belirsiz" size="small" variant="outlined" />;
        if (days < 0) return <Chip label={`${Math.abs(days)} gün geçmiş`} size="small" color="error" />;
        if (days <= 30) return <Chip label={`${days} gün`} size="small" color="warning" />;
        return <Chip label={`${days} gün`} size="small" color="success" variant="outlined" />;
      },
    },
    {
      id: 'companyName',
      label: 'Şirket',
      minWidth: '180px',
      tooltip: (row) => row.companyName || getCompanyName(row.companyId),
      cellSx: { fontWeight: 500 },
      render: (row) => row.companyName || getCompanyName(row.companyId),
    },
    // "Bayi" sütunu yalnız Bayi Yazarkasaları sekmesinde: ana listede tüm
    // satırlar merkez olduğu için boş bir sütun eklemek yer kaybı olurdu.
    ...(isDealerFiscalTab
      ? [{
          id: 'dealerName',
          label: 'Bayi',
          minWidth: '160px',
          tooltip: (row: FiscalDeviceWithCompany) => row.dealerName || '',
          render: (row: FiscalDeviceWithCompany) =>
            row.dealerName ? (
              <Chip label={row.dealerName} size="small" color="secondary" variant="outlined" />
            ) : (
              '-'
            ),
        } as DataTableColumn<FiscalDeviceWithCompany>]
      : []),
    {
      id: 'branch',
      label: 'Şube',
      // Başlık kısa ama şube adları uzun ("Maltepe (Zümrütevler Mah.)" gibi);
      // otomatik ölçüm başlığa göre daraltıyordu.
      minWidth: '200px',
      tooltip: (row) => row.branch || '',
      render: (row) => row.branch || '-',
    },
    {
      // Geçici sütun: şubesi atanmamış kayıtlarda eskiden elle yazılmış metni
      // gösterir. Tüm cihazlara gerçek şube seçildikten sonra kaldırılabilir.
      id: 'legacyBranch',
      label: 'Eski Şube Notu',
      minWidth: '200px',
      tooltip: (row) => row.legacyBranch || '',
      render: (row) =>
        row.legacyBranch ? (
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'warning.dark' }}>
            {row.legacyBranch}
          </Typography>
        ) : (
          '-'
        ),
    },
    { id: 'brand', label: 'Marka', render: (row) => row.brand || '-' },
    { id: 'model', label: 'Model', render: (row) => row.model || '-' },
    {
      id: 'serialNumber',
      label: 'Seri No',
      cellSx: { fontFamily: 'monospace' },
      render: (row) => row.serialNumber || '-',
    },
    { id: 'activationDate', label: 'Aktivasyon', render: (row) => formatDateTR(row.activationDate) },
    { id: 'expiryDate', label: 'Bitiş', render: (row) => formatDateTR(row.expiryDate) },
    { id: 'vkn', label: 'VKN', render: (row) => row.vkn || '-' },
    { id: 'authorizedPerson', label: 'Yetkili', render: (row) => row.authorizedPerson || '-' },
    { id: 'phone', label: 'Telefon', render: (row) => row.phone || '-' },
    {
      id: 'email',
      label: 'E-posta',
      minWidth: '180px',
      tooltip: (row) => row.email || '',
      render: (row) => row.email || '-',
    },
    {
      id: 'lineNumber',
      label: 'Hat No',
      cellSx: { fontFamily: 'monospace' },
      render: (row) => row.lineNumber || '-',
    },
    {
      id: 'lineOwner',
      label: 'Hat Sahibi',
      tooltip: (row) => row.lineOwner || '',
      render: (row) => row.lineOwner || '-',
    },
    { id: 'lineOperator', label: 'Operatör', render: (row) => row.lineOperator || '-' },
    {
      id: 'accountName',
      label: 'Cari',
      minWidth: '160px',
      tooltip: (row) => row.accountName || '',
      render: (row) => row.accountName || '-',
    },
    { id: 'city', label: 'İl', render: (row) => row.city || '-' },
    { id: 'usageArea', label: 'Kullanım Yeri', render: (row) => row.usageArea || '-' },
    {
      id: 'gmuFeeCollected',
      label: 'GMÜ Ücret',
      align: 'center',
      render: (row) => (
        <Chip
          label={row.gmuFeeCollected ? 'Evet' : 'Hayır'}
          size="small"
          color={row.gmuFeeCollected ? 'success' : 'default'}
          variant={row.gmuFeeCollected ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'invoiceStatus',
      label: 'Fatura Durumu',
      tooltip: (row) => row.invoiceStatus || '',
      render: (row) => row.invoiceStatus || '-',
    },
    fiscalEditColumn('actions'),
    // getDaysRemaining/formatDateTR/getCompanyName her render'da yeniden
    // oluşuyor; kolonların şirket adı listesiyle tazelenmesi için companies yeterli.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [fiscalPage, fiscalPageSize, companies, fiscalEditColumn, isDealerFiscalTab]);

  const matchesUsageFilter = (company: Company): boolean => {
    if (!usageStartDate && !usageEndDate) return true;
    const lastLogin = loginActivities[company.id]?.lastLogin as number | undefined;
    if (!lastLogin) return false;
    if (usageStartDate) {
      const start = new Date(`${usageStartDate}T00:00:00`).getTime();
      if (lastLogin < start) return false;
    }
    if (usageEndDate) {
      const end = new Date(`${usageEndDate}T23:59:59.999`).getTime();
      if (lastLogin > end) return false;
    }
    return true;
  };

  const matchesDemoDaysFilter = (company: Company): boolean => {
    if (!demoDaysFilter) return true;
    const days = getDaysRemaining(company.demoUntil || null);
    if (demoDaysFilter === 'none') return !company.demoUntil;
    if (demoDaysFilter === 'expired') {
      if (company.accessState === 'grace') return false;
      return !!company.demoUntil && (company.demoExpired === true || company.accessState === 'expired' || (days !== null && days < 0));
    }
    if (demoDaysFilter === '1-7') return days !== null && days >= 0 && days <= 7;
    if (demoDaysFilter === '8-15') return days !== null && days >= 8 && days <= 15;
    if (demoDaysFilter === '16-30') return days !== null && days >= 16 && days <= 30;
    if (demoDaysFilter === '30+') return days !== null && days > 30;
    return true;
  };

  const matchesStatusFilter = (company: Company): boolean => {
    if (!statusFilter) return true;
    // Pasif = erişim kilitli (demo veya abonelik grace'i bitti). Aktif = kilit yok.
    if (statusFilter === 'passive') return companyAccessLocked(company);
    if (statusFilter === 'active') return !companyAccessLocked(company);
    return true;
  };

  const matchesSharedCompanyFilters = (company: Company): boolean => {
    if (!company || !company.name) return false;
    if (!company.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    if (!matchesUsageFilter(company)) return false;
    if (!matchesDemoDaysFilter(company)) return false;
    if (!matchesStatusFilter(company)) return false;
    return true;
  };

  // Şirketleri filtrele (arama, tab ve ortak kullanım/demo/durum filtreleri)
  const filteredCompanies = (() => {
    if (currentTab === 'all') {
      // Tüm Şirketler — arama kutusuna ek olarak şirket/bayi filtreleri.
      // Şirket/bayi filtreleri sekmeye özel; başka sekmeye geçildiğinde sıfırlanır.
      return companies.filter(company => {
        if (!matchesSharedCompanyFilters(company)) return false;
        if (companyFilter && company.id !== companyFilter.id) return false;
        if (dealerFilter && company.dealerId !== dealerFilter.id) return false;
        return true;
      });
    } else if (currentTab === 'demo') {
      return companies.filter(company => {
        if (!matchesSharedCompanyFilters(company)) return false;
        return !!company.demoUntil;
      });
    } else if (currentTab === 'active') {
      return activeCompanies.filter(company => matchesSharedCompanyFilters(company));
    } else if (currentTab === 'contracts') {
      return companies.filter(company => {
        if (!matchesSharedCompanyFilters(company)) return false;
        return contracts[company.id] !== undefined && contracts[company.id] !== null;
      });
    } else if (currentTab === 'fiscal-companies') {
      return companies.filter(company => {
        if (!matchesSharedCompanyFilters(company)) return false;
        return companiesWithDevices.includes(company.id);
      });
    }
    return [];
  })();

  const displayedCompanies = [...filteredCompanies].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmpText = (left: string, right: string) =>
      left.localeCompare(right, 'tr', { sensitivity: 'base' }) * dir;
    const cmpNumLast = (left?: number | null, right?: number | null) => {
      const aMissing = left == null;
      const bMissing = right == null;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return (left - right) * dir;
    };

    if (sortBy === 'name') return cmpText(a.name || '', b.name || '');
    if (sortBy === 'createdAt') {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : null;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : null;
      return cmpNumLast(aTime, bTime);
    }
    if (sortBy === 'lastLogin') {
      return cmpNumLast(
        loginActivities[a.id]?.lastLogin as number | undefined,
        loginActivities[b.id]?.lastLogin as number | undefined
      );
    }
    if (sortBy === 'demoDays') {
      return cmpNumLast(getDaysRemaining(a.demoUntil || null), getDaysRemaining(b.demoUntil || null));
    }
    if (sortBy === 'maxPersonnel') return ((a.maxPersonnel || 0) - (b.maxPersonnel || 0)) * dir;
    if (sortBy === 'dealer') return cmpText(a.dealerName || '', b.dealerName || '');
    return 0;
  });

  // Şirket detayları modalını açma
  const handleOpenDetailsModal = (company: Company) => {
    setDetailsCompany(company);
    setIsDetailsModalOpen(true);
  };

  // Şirket detayları modalını kapatma
  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setDetailsCompany(null);
  };

  // Liste görünümü için menu state
  const [tableMenuAnchorEl, setTableMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTableCompany, setSelectedTableCompany] = useState<Company | null>(null);

  const handleTableMenuClick = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    event.stopPropagation();
    setTableMenuAnchorEl(event.currentTarget);
    setSelectedTableCompany(company);
  };

  const handleTableMenuClose = () => {
    setTableMenuAnchorEl(null);
    setSelectedTableCompany(null);
  };

  const handleTableMenuEdit = () => {
    if (selectedTableCompany) {
      handleOpenModal(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  const handleTableMenuDelete = () => {
    if (selectedTableCompany) {
      handleDeleteCompany(selectedTableCompany.id, selectedTableCompany.name);
    }
    handleTableMenuClose();
  };

  const handleTableMenuFiscalDevices = () => {
    if (selectedTableCompany) {
      handleOpenFiscalDeviceModal(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  const handleTableMenuSyncToSql = () => {
    if (selectedTableCompany) {
      handleSyncCompanyToSql(selectedTableCompany.id, selectedTableCompany.name);
    }
    handleTableMenuClose();
  };

  // Kart menüsüyle birebir aynı aksiyonlar — liste görünümü için
  const handleTableMenuContract = () => {
    if (selectedTableCompany) {
      handleOpenContractModal(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  const handleTableMenuAiChat = () => {
    if (selectedTableCompany) {
      setAiChatCompany({ id: selectedTableCompany.id, name: selectedTableCompany.name });
    }
    handleTableMenuClose();
  };

  const handleTableMenuModules = () => {
    if (selectedTableCompany) {
      setModulesCompany({ id: selectedTableCompany.id, name: selectedTableCompany.name });
    }
    handleTableMenuClose();
  };

  const handleTableMenuAssignDealer = () => {
    if (selectedTableCompany) {
      handleOpenAssignDealer(selectedTableCompany);
    }
    handleTableMenuClose();
  };

  // Sözleşme modalını aç
  const handleOpenContractModal = (company: Company) => {
    setProductsReady(!isSuperAdmin);
    setSelectedCompanyForContract(company);
    
    // Eğer şirketin mevcut bir sözleşmesi varsa, tarihleri doldur
    const existingContract = contracts[company.id];
    if (existingContract) {
      setContractStartDate(new Date(existingContract.startDate).toISOString().split('T')[0]);
      setContractEndDate(new Date(existingContract.endDate).toISOString().split('T')[0]);
    } else {
      setContractStartDate('');
      setContractEndDate('');
    }
    
    setIsContractModalOpen(true);
  };

  // Sözleşme modalını kapat
  const handleCloseContractModal = () => {
    setIsContractModalOpen(false);
    setSelectedCompanyForContract(null);
    setContractStartDate('');
    setContractEndDate('');
  };

  // Sözleşme kaydet
  const handleSaveContract = async () => {
    if (!selectedCompanyForContract || !contractStartDate || !contractEndDate) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    let contractSaved = false;
    try {
      setSavingContract(true);
      
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.createCompanyContract(
        selectedCompanyForContract.id,
        contractStartDate,
        contractEndDate
      );

      if (result.success) {
        contractSaved = true;
        if (isSuperAdmin && productsEditorRef.current) {
          await productsEditorRef.current.save(contractStartDate, contractEndDate);
        }

        // Sözleşmeleri yeniden yükle
        const contractsResult = await superAdminService.getAllContracts();
        if (contractsResult.success) {
          setContracts(contractsResult.contracts || {});
        }

        handleCloseContractModal();
        alert('Sözleşme başarıyla kaydedildi!');
      }
    } catch (error) {
      console.error('Sözleşme kaydedilirken hata:', error);
      alert(
        contractSaved
          ? 'Sözleşme kaydedildi ancak ürün fiyatları yazılamadı. Pencereyi kapatmadan tekrar deneyin.'
          : 'Sözleşme kaydedilirken bir hata oluştu'
      );
    } finally {
      setSavingContract(false);
    }
  };

  // Gösterge yalnızca arama kutusu hâlâ bildirimden gelen metni taşıyorsa görünür;
  // kullanıcı metni değiştirdiyse filtre artık "bildirimden gelen" sayılmaz.
  // Yazarkasa sekmeleri (merkez ve bayi) ayrı bir arama kutusu kullanıyor.
  const usesFiscalSearchBox =
    reminderFilter?.tab === 'fiscal-devices' || reminderFilter?.tab === 'dealer-fiscal-devices';

  const activeReminderFilter =
    reminderFilter &&
    (usesFiscalSearchBox
      ? fiscalSearchTerm === reminderFilter.text
      : searchTerm === reminderFilter.text)
      ? reminderFilter
      : null;

  const clearReminderFilter = () => {
    if (usesFiscalSearchBox) {
      setFiscalSearchTerm('');
      setFiscalDebouncedSearch('');
      setFiscalPage(0);
    } else {
      setSearchTerm('');
    }
    setReminderFilter(null);
  };

  return (
    <SuperAdminLayout
      title="GTS Süper Admin"
      icon={<BusinessIcon />}
      // Yazarkasa listesi 21 sütunlu; Container'ın xl sınırı tabloyu sayfanın
      // ortasında dar bir kutuya hapsediyordu. Bu sekmede tam genişlik verilir.
      maxWidth={currentTab === 'fiscal-devices' ? false : 'xl'}
    >
          <Box sx={{ 
            mb: 4, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                <TextField
                  variant="outlined"
                  placeholder="Şirket ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Tooltip title="Aramayı temizle">
                          <IconButton size="small" onClick={() => { setSearchTerm(''); setReminderFilter(null); }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  sx={{
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: 400,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  bgcolor: '#1976d2', 
                  color: 'white',
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  minWidth: { xs: '100%', sm: 140 },
                  boxSizing: 'border-box'
                }}>
                  <BusinessIcon fontSize="small" />
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    Toplam Şirket: {displayedCompanies.length}
                  </Typography>
                </Box>
                {/* Bayi kotası — merkezin verdiği şirket açma hakkı. Süper adminde
                    sınır olmadığı için gösterilmez. */}
                {myQuota?.isDealer && (
                  <Tooltip
                    title={
                      myQuota.remainingQuota > 0
                        ? `${myQuota.remainingQuota} şirket daha açabilirsiniz`
                        : 'Kotanız dolu — yeni şirket açmak için merkez ile iletişime geçin'
                    }
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: myQuota.remainingQuota > 0 ? '#2e7d32' : '#c62828',
                      color: 'white',
                      px: 2,
                      py: 1.5,
                      borderRadius: 2,
                      minWidth: { xs: '100%', sm: 150 },
                      boxSizing: 'border-box',
                    }}>
                      <StorefrontIcon fontSize="small" />
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        Şirket Kotası: {myQuota.companyCount} / {myQuota.maxCompanies}
                      </Typography>
                    </Box>
                  </Tooltip>
                )}
                <Button
                  variant="contained"
                  startIcon={<AddBusinessIcon />}
                  onClick={() => setIsCreateCompanyOpen(true)}
                  // Kota dolu bir bayinin isteği sunucuda 403 ile zaten reddediliyor;
                  // buton peşinen kapatılıp sebebi ipucunda söyleniyor.
                  disabled={myQuota?.isDealer === true && myQuota.remainingQuota === 0}
                  sx={{
                    py: 1.4,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    width: { xs: '100%', sm: 'auto' }
                  }}
                >
                  Şirket Oluştur
                </Button>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton 
                color={viewMode === 'card' ? 'primary' : 'default'} 
                onClick={() => setViewMode('card')}
                sx={{ 
                  bgcolor: viewMode === 'card' ? '#1976d2' : 'transparent',
                  color: viewMode === 'card' ? 'white' : 'inherit',
                  '&:hover': { bgcolor: viewMode === 'card' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
                }}
              >
                <ViewModuleIcon />
              </IconButton>
              <IconButton 
                color={viewMode === 'list' ? 'primary' : 'default'} 
                onClick={() => setViewMode('list')}
                sx={{ 
                  bgcolor: viewMode === 'list' ? '#1976d2' : 'transparent',
                  color: viewMode === 'list' ? 'white' : 'inherit',
                  '&:hover': { bgcolor: viewMode === 'list' ? '#1565c0' : 'rgba(0,0,0,0.04)' }
                }}
              >
                <ViewListIcon />
              </IconButton>
            </Box>
          </Box>
          
          {/* Şirket Kategorileri Tab'ları */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Tabs 
              value={currentTab} 
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 500,
                  minHeight: 48,
                },
                '& .Mui-selected': {
                  color: '#1976d2',
                  fontWeight: 600,
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#1976d2',
                  height: 3,
                },
              }}
            >
              <Tab 
                value="all"
                label="Tüm Şirketler"
                icon={<BusinessIcon />}
                iconPosition="start"
              />
              <Tab 
                value="demo"
                label="Demo"
                icon={<HourglassBottomIcon />}
                iconPosition="start"
              />
              {/* Aktif/Sözleşmeli/Yazarkasa sekmeleri süper admine özel uçlara dayanır — bayide gizli */}
              {isSuperAdmin && (
                <Tab 
                  value="active"
                  label="Aktif Şirketler"
                  icon={<BusinessIcon />}
                  iconPosition="start"
                />
              )}
              {/* Sözleşmeler bayiye açık: bayi her müşteri şirketiyle sözleşme girer. */}
              <Tab
                value="contracts"
                label="Sözleşmeli Şirketler"
                icon={<BusinessIcon />}
                iconPosition="start"
              />
              {/* Yazarkasa sekmeleri bayiye açık: bayi kendi verdiği cihazları yönetir.
                  Kapsam backend'de zorlanıyor (bayi merkezin cihazlarını göremez). */}
              <Tab
                value="fiscal-companies"
                label="Yazarkasa Verilen Şirketler"
                icon={<PointOfSaleIcon />}
                iconPosition="start"
              />
              <Tab
                value="fiscal-devices"
                label={isSuperAdmin ? 'Yazarkasalar' : 'Yazarkasalarım'}
                icon={<PointOfSaleIcon />}
                iconPosition="start"
              />
              {/* Bayilerin verdiği cihazlar merkezin ana listesini kirletmesin diye
                  ayrı sekmede; yalnız süper admin görür. */}
              {isSuperAdmin && (
                <Tab
                  value="dealer-fiscal-devices"
                  label="Bayi Yazarkasaları"
                  icon={<StorefrontIcon />}
                  iconPosition="start"
                />
              )}
            </Tabs>
          </Box>

          {currentTab !== 'fiscal-devices' && (
            <Paper
              elevation={1}
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
            >
              {currentTab === 'all' && (
                <Autocomplete
                  size="small"
                  options={companies.map((c) => ({ id: c.id, name: c.name }))}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={companyFilter}
                  onChange={(_, newValue) => setCompanyFilter(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Şirket"
                      placeholder="Şirket seç..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    />
                  )}
                  sx={{ width: { xs: '100%', sm: 240 } }}
                  clearOnEscape
                />
              )}
              {/* Bayi bilgisi yalnızca süper adminde gösteriliyor; bayi kullanıcısı
                  zaten kendi portföyünü görüyor, filtre ona bir şey kazandırmaz. */}
              {currentTab === 'all' && isSuperAdmin && (
                <Autocomplete
                  size="small"
                  options={dealersList}
                  loading={dealersLoading}
                  onOpen={ensureDealersLoaded}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={dealerFilter}
                  onChange={(_, newValue) => setDealerFilter(newValue)}
                  noOptionsText={dealersLoading ? 'Bayiler yükleniyor...' : 'Bayi bulunamadı'}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Bayi"
                      placeholder="Bayi seç..."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {dealersLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ width: { xs: '100%', sm: 220 } }}
                  clearOnEscape
                />
              )}
              {isSuperAdmin && (
                <>
                  <AppDatePicker
                    label="Başlangıç Tarihi"
                    value={usageStartDate}
                    onChange={setUsageStartDate}
                    maxDate={usageEndDate || undefined}
                    size="small"
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                  <AppDatePicker
                    label="Bitiş Tarihi"
                    value={usageEndDate}
                    onChange={setUsageEndDate}
                    minDate={usageStartDate || undefined}
                    size="small"
                    sx={{
                      width: { xs: '100%', sm: 180 },
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' },
                    }}
                  />
                </>
              )}
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                <InputLabel>Demo günü</InputLabel>
                <Select
                  value={demoDaysFilter}
                  label="Demo günü"
                  onChange={(e) => setDemoDaysFilter(e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="none">Demo değil</MenuItem>
                  <MenuItem value="expired">Demo doldu</MenuItem>
                  <MenuItem value="1-7">1–7 gün kaldı</MenuItem>
                  <MenuItem value="8-15">8–15 gün kaldı</MenuItem>
                  <MenuItem value="16-30">16–30 gün kaldı</MenuItem>
                  <MenuItem value="30+">30+ gün kaldı</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={statusFilter}
                  label="Durum"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="passive">Pasif (demo doldu)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
                <InputLabel>Sırala</InputLabel>
                <Select
                  value={sortBy}
                  label="Sırala"
                  onChange={(e) => setSortBy(e.target.value)}
                  MenuProps={{ disableScrollLock: true }}
                >
                  <MenuItem value="name">Şirket adı</MenuItem>
                  <MenuItem value="createdAt">Kayıt tarihi</MenuItem>
                  {isSuperAdmin && <MenuItem value="lastLogin">Son kullanım</MenuItem>}
                  <MenuItem value="demoDays">Demo günü</MenuItem>
                  <MenuItem value="maxPersonnel">Max personel</MenuItem>
                  {isSuperAdmin && <MenuItem value="dealer">Bayi</MenuItem>}
                </Select>
              </FormControl>
              <IconButton
                size="small"
                onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                title={sortDir === 'asc' ? 'Artan' : 'Azalan'}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper',
                }}
              >
                {sortDir === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
              </IconButton>
              {(companyFilter || dealerFilter || usageStartDate || usageEndDate || demoDaysFilter || statusFilter) && (
                <Button
                  size="small"
                  color="inherit"
                  startIcon={<CloseIcon />}
                  onClick={() => {
                    setCompanyFilter(null);
                    setDealerFilter(null);
                    setUsageStartDate('');
                    setUsageEndDate('');
                    setDemoDaysFilter('');
                    setStatusFilter('');
                  }}
                >
                  Filtreleri Temizle
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Chip label={`${displayedCompanies.length} Şirket`} color="primary" size="small" />
            </Paper>
          )}

          {/* API Uyarı Mesajı */}
          {apiWarning && (
            <Alert severity="warning" sx={{ mb: 3, mt: 1 }}>
              <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                ⚠️ companies_index Tablosu Boş
              </Typography>
              <Typography variant="body2">
                {apiWarning}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Firebase Console'dan manuel olarak şirket ID'lerini ekleyin veya yeni şirket kaydı yapıldığında otomatik olarak eklenecektir.
              </Typography>
            </Alert>
          )}
          
          {/* Bildirimden gelinince liste tek kayda iniyor; buradan tek tıkla geri alınır. */}
          {activeReminderFilter && (
            <Alert
              severity="info"
              sx={{ mb: 2, mt: 1 }}
              action={
                <Button color="inherit" size="small" startIcon={<CloseIcon />} onClick={clearReminderFilter}>
                  Filtreyi Kaldır
                </Button>
              }
            >
              Bildirimden gelen filtre uygulandı: <strong>{activeReminderFilter.text}</strong> — listede yalnızca eşleşen kayıtlar var.
            </Alert>
          )}

          {isFiscalDeviceTab ? (
            // Yazarkasalar tab'ı — ön muhasebe sayfalarıyla aynı düzen:
            // filtreler kendi kartında, liste ortak AccountingDataTable'da.
            // Merkez ve bayi listeleri aynı tabloyu paylaşır; fark kaynak
            // parametresi, "Bayi" sütunu ve bayi filtresinden ibaret.
            <Box>
              <Paper elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  variant="outlined"
                  size="small"
                  placeholder="Cihaz, şirket, marka, model, hat no ara..."
                  value={fiscalSearchTerm}
                  onChange={(e) => setFiscalSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start"><SearchIcon /></InputAdornment>
                    ),
                    endAdornment: allFiscalDevicesLoading ? (
                      <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                    ) : fiscalSearchTerm ? (
                      <InputAdornment position="end">
                        <Tooltip title="Aramayı temizle">
                          <IconButton size="small" onClick={() => { setFiscalSearchTerm(''); setReminderFilter(null); }}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: 400, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                />
                <Autocomplete
                  size="small"
                  options={fiscalCompanyOptions}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.id === val.id}
                  value={fiscalCompanyFilter}
                  onChange={(_, newValue) => { setFiscalCompanyFilter(newValue); setFiscalPage(0); }}
                  loading={fiscalDevicesLoading && fiscalCompanyOptions.length === 0}
                  noOptionsText={fiscalDevicesLoading ? 'Yükleniyor...' : 'Yazarkasası olan şirket yok'}
                  renderInput={(params) => (
                    <TextField {...params} label="Şirket" placeholder="Şirket seç..." sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }} />
                  )}
                  sx={{ width: { xs: '100%', sm: 280 } }}
                  clearOnEscape
                />
                <Autocomplete
                  size="small"
                  // Şube kimlikleri şirkete özel; şirket seçilmeden filtrenin
                  // çalışması mümkün değil, o yüzden pasif ama görünür duruyor.
                  disabled={!fiscalCompanyFilter}
                  loading={fiscalBranchOptionsLoading}
                  options={fiscalBranchOptions}
                  getOptionLabel={(opt) => opt.name}
                  isOptionEqualToValue={(opt, val) => opt.branchId === val.branchId}
                  value={fiscalBranchFilter}
                  onChange={(_, newValue) => { setFiscalBranchFilter(newValue); setFiscalPage(0); }}
                  noOptionsText={
                    fiscalBranchOptionsLoading ? 'Şubeler yükleniyor...' : 'Bu şirkete ait şube yok'
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Şube"
                      placeholder={fiscalCompanyFilter ? 'Şube seç...' : 'Önce şirket seçin'}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {fiscalBranchOptionsLoading ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  sx={{ width: { xs: '100%', sm: 260 } }}
                  clearOnEscape
                />
                {/* Bayi filtresi yalnız Bayi Yazarkasaları sekmesinde: ana liste
                    zaten yalnız merkezin cihazlarını gösteriyor. */}
                {isDealerFiscalTab && (
                  <Autocomplete
                    size="small"
                    options={dealersList}
                    loading={dealersLoading}
                    onOpen={ensureDealersLoaded}
                    getOptionLabel={(opt) => opt.name}
                    isOptionEqualToValue={(opt, val) => opt.id === val.id}
                    value={fiscalDealerFilter}
                    onChange={(_, newValue) => { setFiscalDealerFilter(newValue); setFiscalPage(0); }}
                    noOptionsText={dealersLoading ? 'Bayiler yükleniyor...' : 'Bayi bulunamadı'}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Bayi"
                        placeholder="Bayi seç..."
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {dealersLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ width: { xs: '100%', sm: 260 } }}
                    clearOnEscape
                  />
                )}
                <Chip label={`${fiscalTotalCount} Cihaz`} color="primary" />
                {(() => {
                  const expiredCount = allFiscalDevices.filter(d => { const days = getDaysRemaining(d.expiryDate); return days !== null && days < 0; }).length;
                  const warningCount = allFiscalDevices.filter(d => { const days = getDaysRemaining(d.expiryDate); return days !== null && days >= 0 && days <= 30; }).length;
                  return (
                    <>
                      {expiredCount > 0 && <Chip label={`Bu sayfada ${expiredCount} Süresi Dolmuş`} color="error" size="small" />}
                      {warningCount > 0 && <Chip label={`Bu sayfada ${warningCount} Süresi Yaklaşıyor`} color="warning" size="small" />}
                    </>
                  );
                })()}
                <Box sx={{ flexGrow: 1 }} />
                {/* Bayi Yazarkasaları sekmesinde ekleme butonu yok: süper adminin
                    eklediği cihaz merkez damgası alır ve bu listede görünmez;
                    buton kullanıcıyı yanıltırdı. Merkez cihazı ana sekmeden eklenir. */}
                {!isDealerFiscalTab && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => { setFiscalFormDevice(null); setFiscalFormOpen(true); }}
                  >
                    Yeni Cihaz Ekle
                  </Button>
                )}
                <Button variant="outlined" size="small" onClick={loadAllFiscalDevices} startIcon={<SyncIcon />}>Yenile</Button>
                <Button variant="outlined" size="small" color="secondary" onClick={() => setIsIntegrationsModalOpen(true)}>
                  Entegrasyonlar
                </Button>
              </Paper>

              <AccountingDataTable<FiscalDeviceWithCompany>
                columns={fiscalDeviceColumns}
                rows={allFiscalDevices}
                getRowId={(row) => String(row.id)}
                loading={allFiscalDevicesLoading}
                ariaLabel="Yazarkasa cihazları"
                // Sütunlar içeriğe göre genişlesin. Sürüklenebilir genişlik
                // (columnResizeStorageKey) kullanılmıyor: başlığa göre ölçüp
                // kaydettiği için "Şube" gibi başlığı kısa ama içeriği uzun
                // sütunlar okunamayacak kadar daralıyordu.
                tableLayout="auto"
                emptyIcon={<PointOfSaleIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
                emptyMessage={
                  (fiscalSearchTerm || fiscalCompanyFilter || fiscalBranchFilter || fiscalDealerFilter)
                    ? 'Arama/filtre kriterlerinize uygun cihaz bulunamadı.'
                    : isDealerFiscalTab
                      ? 'Bayiler henüz yazarkasa cihazı girmemiş.'
                      : 'Henüz yazarkasa cihazı eklenmemiş.'
                }
                getRowSx={(row) => {
                  const days = getDaysRemaining(row.expiryDate);
                  if (days === null) return {};
                  if (days < 0) return { bgcolor: 'rgba(244,67,54,0.08)', '&:hover': { bgcolor: 'rgba(244,67,54,0.14)' } };
                  if (days <= 30) return { bgcolor: 'rgba(255,152,0,0.08)', '&:hover': { bgcolor: 'rgba(255,152,0,0.14)' } };
                  return {};
                }}
                serverSidePagination
                totalCount={fiscalTotalCount}
                page={fiscalPage}
                pageSize={fiscalPageSize}
                rowsPerPageOptions={[10, 25, 50, 100, 200, 300, 400, 500, 1000]}
                onPageChange={(newPage, newPageSize) => {
                  setFiscalPage(newPage);
                  setFiscalPageSize(newPageSize);
                }}
              />
            </Box>
          ) : (loading || (currentTab === 'active' && activeCompaniesLoading) || (currentTab === 'fiscal-companies' && fiscalDevicesLoading)) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
              <CircularProgress />
              {currentTab === 'active' && activeCompaniesLoading && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Aktif şirketler yükleniyor...
                </Typography>
              )}
              {currentTab === 'fiscal-companies' && fiscalDevicesLoading && (
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Yazarkasa verilen şirketler yükleniyor...
                </Typography>
              )}
            </Box>
          ) : displayedCompanies.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center',
              mt: 8,
              p: 4
            }}>
              <BusinessIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 1, color: 'text.secondary' }}>
                {currentTab === 'active' 
                  ? 'Aktif Şirket Bulunamadı'
                  : currentTab === 'contracts'
                  ? 'Sözleşmeli Şirket Bulunamadı'
                  : currentTab === 'fiscal-companies'
                  ? 'Yazarkasa Verilen Şirket Bulunamadı'
                  : currentTab === 'demo'
                  ? 'Demo Şirket Bulunamadı'
                  : 'Şirket Bulunamadı'
                }
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                {currentTab === 'active' 
                  ? 'Son 7 günde giriş yapan şirket bulunamadı.'
                  : currentTab === 'contracts'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun sözleşmeli şirket bulunamadı.'
                      : 'Henüz sözleşme yapılmış şirket bulunmuyor.'
                    )
                  : currentTab === 'fiscal-companies'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun yazarkasa verilen şirket bulunamadı.'
                      : 'Henüz yazarkasa cihazı verilmiş şirket bulunmuyor.'
                    )
                  : currentTab === 'demo'
                  ? (searchTerm
                      ? 'Arama kriterlerinize uygun demo şirket bulunamadı.'
                      : 'Henüz demo süresi tanımlanmış şirket bulunmuyor. Şirket kartındaki "Modüller" diyaloğundan demo süresi verebilirsiniz.'
                    )
                  : (searchTerm || companyFilter || dealerFilter || usageStartDate || usageEndDate || demoDaysFilter || statusFilter)
                    ? 'Arama veya filtre kriterlerinize uygun şirket bulunamadı.'
                    : 'Sistemde kayıtlı şirket bulunmuyor.'
                }
              </Typography>
              {!searchTerm && apiWarning && currentTab === 'all' && (
                <Typography variant="body2" sx={{ mt: 2, color: 'warning.main', textAlign: 'center' }}>
                  Yeni şirket kaydı yapın veya mevcut şirketlerin ID'lerini companies_index tablosuna ekleyin.
                </Typography>
              )}
            </Box>
          ) : viewMode === 'card' ? (
            // Kart listesi sayfanın kendi kaydırma çubuğunu kullanır; yükseklik
            // sınırı verilirse içeride ikinci bir kaydırma alanı oluşuyordu.
            <Box sx={{ pb: 8 }}>
              <Grid container spacing={2}>
                {displayedCompanies.map((company) => (
                  <Grid 
                    item 
                    xs={12} 
                    sm={6} 
                    md={4} 
                    lg={3} 
                    xl={2} 
                    key={company.id}
                    onClick={() => handleOpenDetailsModal(company)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <CompanyCard
                      {...company}
                      contract={contracts[company.id] || null}
                      loginActivity={currentTab === 'active' ? loginActivities[company.id] : null}
                      aiChatEnabled={aiChatRows[company.id]?.enabled === true}
                      dealerName={isSuperAdmin ? company.dealerName : undefined}
                      showContractDealer={isSuperAdmin}
                      demoUntil={company.demoUntil}
                      demoExpired={company.demoExpired}
                      awaitingModules={company.awaitingModules}
                      onViewDetails={() => handleOpenModal(company)}
                      onModules={(e?: React.MouseEvent) => {
                        setModulesCompany({ id: company.id, name: company.name });
                      }}
                      /* Aşağıdaki aksiyonlar süper admine özel — bayi görünümünde menüde çıkmaz */
                      onDelete={isSuperAdmin ? handleDeleteCompany : undefined}
                      onSyncToSql={isSuperAdmin ? handleSyncCompanyToSql : undefined}
                      onCreateContract={() => handleOpenContractModal(company)}
                      onFiscalDevices={isSuperAdmin ? () => handleOpenFiscalDeviceModal(company) : undefined}
                      onAiChat={isSuperAdmin ? () => setAiChatCompany({ id: company.id, name: company.name }) : undefined}
                      onAssignDealer={isSuperAdmin ? () => handleOpenAssignDealer(company) : undefined}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <CompanyListView
              companies={displayedCompanies}
              isSuperAdmin={isSuperAdmin}
              contracts={contracts}
              syncingCompanyId={syncingCompanyId}
              selectedTableCompany={selectedTableCompany}
              tableMenuAnchorEl={tableMenuAnchorEl}
              selectedAiChatEnabled={selectedTableCompany ? aiChatRows[selectedTableCompany.id]?.enabled === true : false}
              onRowClick={handleOpenDetailsModal}
              onMenuClick={handleTableMenuClick}
              onMenuClose={handleTableMenuClose}
              onMenuEdit={handleTableMenuEdit}
              onMenuFiscalDevices={handleTableMenuFiscalDevices}
              onMenuContract={handleTableMenuContract}
              onMenuAiChat={handleTableMenuAiChat}
              onMenuModules={handleTableMenuModules}
              onMenuAssignDealer={handleTableMenuAssignDealer}
              onMenuSyncToSql={handleTableMenuSyncToSql}
              onMenuDelete={handleTableMenuDelete}
            />
          )}

      {/* Personel Sayısı Güncelleme Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isModalOpen}>
          <Box sx={ModalStyle}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#1976d2',
              color: 'white'
            }}>
              <Typography variant="h6">
                Personel Sayısı Güncelleme
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleCloseModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ p: 3 }}>
              {selectedCompany && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={selectedCompany.logo}
                      alt={selectedCompany.name}
                      sx={{ width: 50, height: 50, mr: 2, bgcolor: '#1976d2' }}
                    >
                      {selectedCompany.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedCompany.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCompany.admin.name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Mevcut Maksimum Personel Sayısı: <strong>{selectedCompany.maxPersonnel || 0}</strong>
                  </Typography>
                  
                  <TextField
                    fullWidth
                    label="Yeni Maksimum Personel Sayısı"
                    variant="outlined"
                    type="number"
                    value={newMaxPersonnel}
                    onChange={(e) => setNewMaxPersonnel(parseInt(e.target.value) || 0)}
                    inputProps={{ min: 0 }}
                    sx={{ mb: 3 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCloseModal}
                      startIcon={<CloseIcon />}
                    >
                      İptal
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleUpdateMaxPersonnel}
                      disabled={updating}
                      startIcon={<SaveIcon />}
                      sx={{
                        bgcolor: '#1976d2',
                        '&:hover': {
                          bgcolor: '#1565c0'
                        }
                      }}
                    >
                      {updating ? 'Güncelleniyor...' : 'Güncelle'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* Şirket Silme Modal */}
      <Modal
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
        disableEscapeKeyDown={isDeleting}
      >
        <Fade in={isDeleteModalOpen}>
          <Box sx={ModalStyle}>
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#f44336',
              color: 'white'
            }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeleteIcon /> Şirket Silme
              </Typography>
              {!isDeleting && (
                <IconButton 
                  size="small" 
                  onClick={closeDeleteModal}
                  sx={{ color: 'white' }}
                >
                  <CloseIcon />
                </IconButton>
              )}
            </Box>
            
            <Box sx={{ p: 3 }}>
              {isDeleting ? (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Şirket Siliniyor: {companyToDelete?.name}
                  </Typography>
                  <LinearProgress sx={{ mb: 3 }} />
                  
                  <Box sx={{ 
                    maxHeight: 300, 
                    overflowY: 'auto', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: 1,
                    p: 2,
                    mb: 2,
                    bgcolor: '#f5f5f5',
                    fontFamily: 'monospace',
                    fontSize: '0.85rem'
                  }}>
                    {deleteProgress.map((msg, idx) => (
                      <Box key={idx} sx={{ py: 0.5 }}>
                        {msg}
                      </Box>
                    ))}
                  </Box>
                  
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Silme işlemi devam ediyor, lütfen bekleyin...
                  </Alert>
                </Box>
              ) : (
                <>
                  {/* Sözleşme Kontrolü - Sözleşmeli şirketler silinemez */}
                  {companyToDelete && hasActiveContract(companyToDelete.id) ? (
                    <>
                      <Alert severity="error" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <WarningIcon color="error" /> 
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Bu Şirket Silinemez!
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Bu şirket ile aktif bir sözleşme bulunmaktadır. Sözleşmeli şirketler silinemez.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                          Lütfen sözleşmenin bitmesini bekleyiniz veya sözleşmeyi iptal ediniz.
                        </Typography>
                      </Alert>
                      
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: '#fff3e0', 
                        borderRadius: 1, 
                        border: '1px solid #ffb74d',
                        mb: 3
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          📋 Sözleşme Bilgileri:
                        </Typography>
                        <Typography variant="body2">
                          <strong>Şirket:</strong> {companyToDelete.name}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Başlangıç:</strong> {new Date(contracts[companyToDelete.id].startDate).toLocaleDateString('tr-TR')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Bitiş:</strong> {new Date(contracts[companyToDelete.id].endDate).toLocaleDateString('tr-TR')}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Kalan Gün:</strong> {contracts[companyToDelete.id].daysRemaining} gün
                        </Typography>
                        {contracts[companyToDelete.id].isExpired && (
                          <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 'bold', mt: 1 }}>
                            ⚠️ Sözleşme süresi dolmuş!
                          </Typography>
                        )}
                      </Box>
                      
                      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Şirket adını yazma formu devre dışı bırakılmıştır.
                      </Typography>
                      
                      <TextField
                        fullWidth
                        variant="outlined"
                        placeholder={companyToDelete.name}
                        value=""
                        disabled={true}
                        sx={{ mb: 3, bgcolor: '#f5f5f5' }}
                      />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button 
                          variant="outlined" 
                          onClick={closeDeleteModal}
                          startIcon={<CloseIcon />}
                        >
                          Kapat
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Alert severity="warning" sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <WarningIcon color="error" /> 
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            Bu işlem geri alınamaz!
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Şirketi sildiğinizde, tüm şirket verileri kalıcı olarak silinecektir. 
                          Personel hesapları korunacak, ancak şirket bilgileri silinecektir.
                        </Typography>
                      </Alert>
                      
                      {companyToDelete && (
                        <>
                          <Typography variant="h6" gutterBottom>
                            {companyToDelete.name} şirketini silmek istediğinize emin misiniz?
                          </Typography>
                          
                          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                            Onaylamak için şirket adını yazın: <strong>{companyToDelete.name}</strong>
                          </Typography>
                          
                          <TextField
                            fullWidth
                            variant="outlined"
                            placeholder={companyToDelete.name}
                            value={deleteConfirmation}
                            onChange={(e) => setDeleteConfirmation(e.target.value)}
                            error={!!deleteConfirmation && deleteConfirmation !== companyToDelete.name}
                            helperText={deleteConfirmation && deleteConfirmation !== companyToDelete.name ? 'Şirket adı doğru değil' : ''}
                            sx={{ mb: 3 }}
                          />
                          
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button 
                              variant="outlined" 
                              onClick={closeDeleteModal}
                              startIcon={<CloseIcon />}
                            >
                              İptal
                            </Button>
                            <Button 
                              variant="contained" 
                              color="error"
                              onClick={confirmDelete}
                              disabled={deleteConfirmation !== companyToDelete.name}
                              startIcon={<DeleteIcon />}
                            >
                              Şirketi Sil
                            </Button>
                          </Box>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>
      
      {/* Şirket Detayları Modal */}
      <CompanyDetailsModal
        open={isDetailsModalOpen}
        onClose={handleCloseDetailsModal}
        company={detailsCompany}
      />

      {/* Yazarkasa Cihazları Modal */}
      {fiscalDeviceCompany && (
        <FiscalDeviceModal
          open={isFiscalDeviceModalOpen}
          onClose={handleCloseFiscalDeviceModal}
          companyId={fiscalDeviceCompany.id}
          companyName={fiscalDeviceCompany.name}
        />
      )}

      {/* Yazarkasalar sekmesinden cihaz ekleme/düzenleme */}
      <FiscalDeviceFormDialog
        open={fiscalFormOpen}
        onClose={() => setFiscalFormOpen(false)}
        onSaved={() => {
          loadAllFiscalDevices();
          loadCompaniesWithFiscalDevices();
        }}
        device={fiscalFormDevice}
        companies={companies.map(c => ({ id: c.id, name: c.name }))}
        defaultCompanyId={fiscalCompanyFilter?.id || null}
      />

      {/* Entegrasyon Genel Bakış Modalı (şube bazında entegrasyonlar + arama) */}
      <IntegrationsOverviewModal
        open={isIntegrationsModalOpen}
        onClose={() => setIsIntegrationsModalOpen(false)}
        companies={companies.map(c => ({ id: c.id, name: c.name }))}
      />


      {/* Şirket Sözleşmesi Modal */}
      <Modal
        open={isContractModalOpen}
        onClose={handleCloseContractModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={isContractModalOpen}>
          <Box
            sx={{
              ...ModalStyle,
              width: { xs: '94%', sm: 640, md: 760 },
              maxHeight: '92vh',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ 
              p: 2, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              bgcolor: '#66bb6a',
              color: 'white'
            }}>
              <Typography variant="h6">
                📄 Şirket Sözleşmesi
              </Typography>
              <IconButton 
                size="small" 
                onClick={handleCloseContractModal}
                sx={{ color: 'white' }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
              {selectedCompanyForContract && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={selectedCompanyForContract.logo}
                      alt={selectedCompanyForContract.name}
                      sx={{ width: 50, height: 50, mr: 2, bgcolor: '#66bb6a' }}
                    >
                      {selectedCompanyForContract.name.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6">{selectedCompanyForContract.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedCompanyForContract.admin.name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <AppDatePicker
                      fullWidth
                      label="Sözleşme Başlangıç Tarihi"
                      value={contractStartDate}
                      onChange={setContractStartDate}
                      size="medium"
                    />
                    <AppDatePicker
                      fullWidth
                      label="Sözleşme Bitiş Tarihi"
                      value={contractEndDate}
                      onChange={setContractEndDate}
                      size="medium"
                    />
                  </Box>
                  
                  {contracts[selectedCompanyForContract.id] && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Mevcut sözleşme: {new Date(contracts[selectedCompanyForContract.id].startDate).toLocaleDateString('tr-TR')} - {new Date(contracts[selectedCompanyForContract.id].endDate).toLocaleDateString('tr-TR')}
                      </Typography>
                      <Typography variant="body2">
                        Kalan gün: <strong>{contracts[selectedCompanyForContract.id].daysRemaining}</strong>
                      </Typography>
                      {/* Sözleşmeyi kim girdi — merkez bayi satışlarını ayırt etsin. */}
                      {isSuperAdmin && contracts[selectedCompanyForContract.id].dealerName && (
                        <Typography variant="body2">
                          Girişi yapan bayi: <strong>{contracts[selectedCompanyForContract.id].dealerName}</strong>
                        </Typography>
                      )}
                    </Alert>
                  )}

                  {isSuperAdmin && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <CompanyProductsEditor
                        key={selectedCompanyForContract.id}
                        ref={productsEditorRef}
                        companyId={selectedCompanyForContract.id}
                        onReadyChange={handleProductsReady}
                      />
                    </>
                  )}
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                    <Button 
                      variant="outlined" 
                      onClick={handleCloseContractModal}
                      startIcon={<CloseIcon />}
                    >
                      İptal
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={handleSaveContract}
                      disabled={savingContract || !productsReady || !contractStartDate || !contractEndDate}
                      startIcon={<SaveIcon />}
                      sx={{
                        bgcolor: '#66bb6a',
                        '&:hover': {
                          bgcolor: '#57a05a'
                        }
                      }}
                    >
                      {savingContract ? 'Kaydediliyor...' : 'Kaydet'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        </Fade>
      </Modal>

      {/* SQL Senkronizasyon Snackbar */}
      <Snackbar
        open={syncSnackbar.open}
        autoHideDuration={syncSnackbar.severity === 'info' ? null : 4000}
        onClose={() => setSyncSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSyncSnackbar(prev => ({ ...prev, open: false }))}
          severity={syncSnackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {syncSnackbar.message}
        </Alert>
      </Snackbar>

      {/* AI Chat Asistanı ayarları (aç/kapat + limit + kullanım + konuşma dökümü) */}
      <AiChatSettingsDialog
        open={aiChatCompany !== null}
        onClose={() => setAiChatCompany(null)}
        company={aiChatCompany}
        row={aiChatCompany ? aiChatRows[aiChatCompany.id] || null : null}
        onChanged={fetchAiChatRows}
      />

      {/* Modül yönetimi (şirket bazlı modül aç/kapa) */}
      <ModuleSettingsDialog
        open={modulesCompany !== null}
        onClose={() => setModulesCompany(null)}
        company={modulesCompany}
      />

      {/* Şirket oluşturma (superadmin + bayi) */}
      <CreateCompanyDialog
        open={isCreateCompanyOpen}
        onClose={() => setIsCreateCompanyOpen(false)}
        onCreated={async () => {
          await reloadCompanies();
          // Bayi, şirketi açtıktan sonra modül izni istemeyi unutmasın. 'info'
          // seviyesi kendiliğinden kapanmıyor; bayi okuyup kapatmak zorunda.
          if (!isSuperAdmin) {
            setSyncSnackbar({
              open: true,
              severity: 'info',
              message: `Şirket oluşturuldu. Modülleri kapalı geldi — ${BRAND.copyrightHolder} yönetiminden bu şirket için modül izni isteyiniz.`,
            });
          }
        }}
        quota={myQuota}
      />

      {/* Bayiye Ata (yalnız süper admin — kart menüsü kısayolu) */}
      <Dialog open={assignDealerCompany !== null} onClose={closeAssignDealer} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: '#7b1fa2', color: 'white', py: 1.5 }}>
          Bayiye Ata — {assignDealerCompany?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {assignDealerCompany?.dealerName && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Şu anki bayi: <strong>{assignDealerCompany.dealerName}</strong>. Yeni bir bayi seçerseniz atama taşınır.
            </Alert>
          )}
          <Autocomplete
            fullWidth
            size="small"
            loading={dealersLoading}
            options={dealersList.filter((d) => d.id !== assignDealerCompany?.dealerId)}
            getOptionLabel={(d) => d.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={selectedDealer}
            onChange={(_e, value) => setSelectedDealer(value)}
            renderInput={(params) => (
              <TextField {...params} label="Bayi seç..." placeholder="Bayi adı yazın" />
            )}
            noOptionsText={dealersLoading ? 'Yükleniyor...' : 'Bayi bulunamadı'}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          {assignDealerCompany?.dealerId && (
            <Button
              color="error"
              onClick={handleUnassignDealer}
              disabled={assigningDealer}
              sx={{ mr: 'auto' }}
            >
              Atamayı Kaldır
            </Button>
          )}
          <Button onClick={closeAssignDealer} disabled={assigningDealer}>İptal</Button>
          <Button
            variant="contained"
            onClick={handleAssignDealer}
            disabled={!selectedDealer || assigningDealer}
            startIcon={assigningDealer ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Ata
          </Button>
        </DialogActions>
      </Dialog>

      {/* Canlı destek: sayfa açıkken yeni talepler sesli uyarıyla düşer,
          ilk devralan yetkili görüşmeyi üstlenir (SignalR /hubs/support).
          İlk fazda süper admine özel — bayide render edilmez. */}
      {isSuperAdmin && <LiveSupportPanel />}
    </SuperAdminLayout>
  );
};

export default SuperAdmin; 